import { LegalLayout, H2, P, UL } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" effective="June 2026">
      <P>
        This policy explains how Pocket Docta Ltd ("PocketDocta", "we", "us") collects and uses your
        personal data. Pocket Docta is one app with two modes: <strong>Lifestyle</strong> (global
        travel health and accessibility planning) and <strong>Care</strong> (specialist
        second-opinion healthcare access, launching in Cameroon, Gabon, Ghana, Ivory Coast, Kenya
        and Rwanda). This policy covers both.
      </P>

      <H2>1. Who we are</H2>
      <P>
        Pocket Docta Ltd (Company No. 17356731), registered at 71 Alderton Road, London CR0 6HL,
        United Kingdom, is the data controller responsible for your personal data. For any privacy
        query, contact us at info@pocketdocta.com.
      </P>

      <H2>2. What data we collect</H2>
      <P><strong>2.1 Account information.</strong> Email address, password (stored encrypted, never in plain text), and basic account settings.</P>
      <P>
        <strong>2.2 Health profile data — Special Category Data.</strong> When you complete your
        health profile, we collect: health conditions, medications, allergies, mobility needs,
        sensory needs, nationality, country of residence, insurance provider details, vaccination
        status, and an emergency contact name and phone number. This is Special Category Data under
        UK GDPR (data concerning health). We only collect it with your explicit, separate consent,
        and every field is optional — you can use core parts of the app without providing it, though
        some features (personalised reports, Care case creation) will be limited without it.
      </P>
      <P>
        <strong>2.3 Medical documents.</strong> Any documents you upload (medical letters,
        vaccination certificates, test results) are stored in your account. If you opt in, we use AI
        to extract relevant details from these documents to pre-fill parts of your profile — this
        extraction is optional and can be switched off.
      </P>
      <P>
        <strong>2.4 Care-specific data.</strong> If you use Care mode: your case description, any
        documents or health information you attach to a case, the name and details of your chosen
        local doctor, and — once your case is reviewed — the specialist's written opinion. If you are
        a local doctor or specialist using the Care professional portal, we additionally collect your
        licence number, issuing body, licence issue and expiry dates, and a copy of your licence
        document for verification purposes.
      </P>
      <P>
        <strong>2.5 Payment information.</strong> Payments are processed by Stripe and PayPal. We do
        not store your full card details. We retain a record of transactions (amount, date, what was
        paid for) for accounting and legal purposes. If someone else pays for your subscription or
        case (a "third-party payer"), they only ever see your first name, the item being paid for,
        and the price — they never see any of your health information, documents, or case content.
      </P>
      <P>
        <strong>2.6 Device and usage data.</strong> Standard technical data such as device type,
        operating system, app version, and usage analytics (which screens are used, error logs). We
        use PostHog, a product-analytics provider, to collect this — including session replay
        (recordings of in-app interactions) — to help us maintain and improve the app. See section 13.
      </P>
      <P>
        <strong>2.7 Location data.</strong> If you use Lifestyle's trip planning features, we ask for
        the destination(s) you're travelling to, which you provide directly. In Care mode, if you
        choose "Use my current location", we use your device's location once to detect your country
        and city so we can match local doctors — we do not track your real-time location in the
        background.
      </P>

      <H2>3. How we use your data</H2>
      <UL items={[
        "To provide the core service: generating your Travel Confidence Reports, matching and delivering Care specialist opinions, storing your documents, managing bookings and cases.",
        "To communicate with you: account notifications, case status updates, service messages.",
        "To improve the app and fix problems.",
        "To meet our legal and regulatory obligations, including healthcare record-keeping requirements that may apply to Care case data.",
      ]} />
      <P>We do not use your data for advertising, and we do not sell your personal data to third parties.</P>

      <H2>4. Our legal basis for processing (UK GDPR)</H2>
      <UL items={[
        "Consent — for Special Category health data (Article 9(2)(a)), and for optional features like AI document extraction. You can withdraw consent at any time via your account settings; this does not affect processing already carried out.",
        "Contract — processing needed to provide the service you've signed up for (Article 6(1)(b)).",
        "Legal obligation — where we must retain records for regulatory, accounting, or healthcare governance reasons (Article 6(1)(c)).",
        "Legitimate interests — for basic security, fraud prevention, and service improvement, balanced against your rights (Article 6(1)(f)).",
      ]} />

      <H2>5. AI and automated processing</H2>
      <P>
        Lifestyle's Travel Confidence Reports are generated with AI assistance (currently Anthropic's
        Claude), using your health profile and destination information to produce personalised
        guidance. This is informational content, not medical advice.
      </P>
      <P>
        Care's specialist opinions are written entirely by human specialist doctors — never by AI. AI
        is not used to diagnose, recommend treatment, or generate any part of a specialist's written
        opinion. Any future internal use of AI to help specialists organise case records before
        writing their opinion would remain an internal drafting aid only, never a substitute for the
        specialist's own clinical judgement, and would be disclosed here if introduced.
      </P>

      <H2>6. Who we share your data with</H2>
      <UL items={[
        "Your chosen local doctor and, for Care cases, the matched specialist — only the case information and documents you explicitly select to share, never your full account or unrelated health history.",
        "Payment processors (Stripe, PayPal) — to process payments; governed by their own privacy policies.",
        "Hosting and infrastructure providers — who store and run the app on our behalf, under data processing agreements requiring them to protect your data to UK GDPR standards.",
        "Regulators or law enforcement — only where we are legally required to disclose information.",
      ]} />
      <P>We do not share your health data with employers, insurers (beyond what you explicitly submit for network-matching purposes), advertisers, or data brokers.</P>

      <H2>7. International data transfers</H2>
      <P>
        Because Care connects patients with specialists who may be located anywhere in the world, and
        because Care itself operates across multiple countries, your data may be processed outside the
        UK. Where this happens, we rely on UK GDPR-approved safeguards — such as the UK's
        International Data Transfer Agreement or adequacy regulations — to ensure your data continues
        to receive an equivalent standard of protection.
      </P>

      <H2>8. Data security</H2>
      <P>
        We use encryption for data in transit and at rest, role-based access controls (so, for
        example, a specialist can only see cases assigned to them), and restrict access to health
        data to what each part of the system genuinely needs. No system is 100% secure, and we will
        notify affected users and the ICO of any breach as required by law.
      </P>

      <H2>9. How long we keep your data</H2>
      <UL items={[
        "Account and health profile data: retained while your account is active. When you delete your account in the app it is removed immediately; deletion requests made by email are completed within 30 days. We may retain limited records where legally required (see below).",
        "Care case records and specialist opinions: retained for a period aligned with the medical record-keeping requirements of each Care launch country (being finalised with local legal advisers), even after a case is closed, as these may carry independent record-keeping obligations separate from your account.",
        "Payment records: retained as required by UK accounting and tax law (typically 6 years).",
      ]} />

      <H2>10. Your rights</H2>
      <P>
        Under UK GDPR, you have the right to: access the data we hold about you; correct inaccurate
        data; request deletion ("right to be forgotten"); restrict or object to processing; receive
        your data in a portable format; and withdraw consent at any time. You can exercise most of
        these directly from your account settings — "Delete all my data" removes your profile,
        documents, trips, reports, cases, and consultation history. For anything else, contact us at
        info@pocketdocta.com. You also have the right to complain to the UK Information Commissioner's
        Office (ico.org.uk) if you believe we have not handled your data properly.
      </P>

      <H2>11. Children's privacy</H2>
      <P>
        Pocket Docta is not intended for use by anyone under 18. We do not knowingly collect data
        from children. If you believe a child has provided us with personal data, contact us and we
        will delete it.
      </P>

      <H2>12. Third-party content</H2>
      <P>
        Lifestyle's Resources section links to health and wellbeing videos produced by third-party
        organisations (such as the NHS or condition-specific charities). These are not created or
        verified by Pocket Docta, and viewing them is subject to that organisation's own privacy
        practices, not this policy.
      </P>

      <H2>13. Cookies, analytics and similar technologies</H2>
      <P>
        We use strictly necessary cookies and local storage to log you in and keep you signed in
        during a session. We also use PostHog, a product-analytics tool, to understand how the app is
        used and to diagnose problems — this includes anonymised usage events and session replay
        (recordings of in-app interactions). PostHog processes this data on our behalf under a data
        processing agreement. We do not use advertising or marketing cookies, and we do not sell your
        personal data.
      </P>

      <H2>14. Changes to this policy</H2>
      <P>
        We may update this policy as the app evolves, particularly as Care expands into new countries
        with their own regulatory requirements. We will notify you of material changes via the app or
        by email before they take effect.
      </P>

      <H2>15. Contact us</H2>
      <P>
        Questions about this policy or how we handle your data: info@pocketdocta.com. Postal address:
        71 Alderton Road, London CR0 6HL, United Kingdom.
      </P>
    </LegalLayout>
  );
}
